import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { absUrl } from "@/lib/api";
import { toast } from "sonner";

const TABS = ["partners", "tours", "users"];

export default function Admin() {
  const [tab, setTab] = useState("partners");
  const [partners, setPartners] = useState([]);
  const [tours, setTours] = useState([]);
  const [users, setUsers] = useState([]);
  const [uploadType, setUploadType] = useState("itinerary");

  const load = async () => {
    try { setPartners((await api.get("/admin/partners")).data || []); } catch {}
    try { setTours((await api.get("/admin/tours")).data || []); } catch {}
    try { setUsers((await api.get("/admin/users")).data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const action = async (path, msg) => {
    try { await api.post(path); toast.success(msg); load(); }
    catch { toast.error("Action failed"); }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success(`Role changed to ${role}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change role");
      load();
    }
  };

  const onAdminUpload = async (userId, file) => {
    const fd = new FormData();
    fd.append("document_type", uploadType);
    fd.append("file", file);
    try {
      await api.post(`/admin/documents/upload/${userId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to user's dashboard");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="pt-28 pb-24 max-w-6xl mx-auto px-6 md:px-12" data-testid="admin-page">
        <div className="t-overline text-seafoam mb-5">Admin</div>
        <h1 className="t-display text-slate-900"><b>Operations.</b></h1>

        <div className="mt-10 flex gap-2 flex-wrap">
          {TABS.map((tt) => (
            <button key={tt} data-testid={`admin-tab-${tt}`} onClick={() => setTab(tt)} className={`px-5 py-2 rounded-full text-sm capitalize ${tab === tt ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
              {tt}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "partners" && (
            <div className="grid md:grid-cols-2 gap-5" data-testid="admin-partners">
              {partners.length === 0 && <div className="text-slate-500">No applications.</div>}
              {partners.map((p) => (
                <div key={p.partner_id} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-base font-medium text-slate-900"><b>{p.company_name}</b></div>
                      <div className="text-xs text-slate-500 mt-1 capitalize">{p.status}</div>
                    </div>
                    {p.status === "pending" && (
                      <div className="flex gap-2">
                        <button data-testid={`approve-partner-${p.partner_id}`} onClick={() => action(`/admin/partners/${p.partner_id}/approve`, "Partner approved")} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs">Approve</button>
                        <button data-testid={`reject-partner-${p.partner_id}`} onClick={() => action(`/admin/partners/${p.partner_id}/reject`, "Rejected")} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs">Reject</button>
                      </div>
                    )}
                  </div>
                  {p.description && <p className="text-sm text-slate-600 mt-4">{p.description}</p>}
                </div>
              ))}
            </div>
          )}

          {tab === "tours" && (
            <div className="grid md:grid-cols-2 gap-5" data-testid="admin-tours">
              {tours.map((tr) => (
                <div key={tr.tour_id} className="bg-white rounded-2xl p-6 shadow-sm flex items-start gap-4">
                  <img src={absUrl(tr.cover_image_url)} alt="" className="w-24 h-24 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="t-overline text-seafoam">{tr.category}</div>
                    <div className="text-base font-medium mt-1"><b>{tr.title}</b></div>
                    <div className="flex items-center justify-between mt-3">
                      <span className={`text-xs px-3 py-1 rounded-full ${tr.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{tr.status}</span>
                      {tr.status !== "active" && (
                        <button data-testid={`approve-tour-${tr.tour_id}`} onClick={() => action(`/admin/tours/${tr.tour_id}/approve`, "Tour approved")} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs">Approve</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "users" && (
            <div data-testid="admin-users">
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-6 flex items-center gap-4">
                <span className="t-overline text-slate-500">Upload type</span>
                <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm">
                  <option value="itinerary">Itinerary</option>
                  <option value="voucher">Voucher</option>
                  <option value="eticket">E-ticket</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {users.map((u) => (
                  <div key={u.user_id} className="bg-white rounded-2xl p-5 shadow-sm flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-medium shrink-0">{u.name?.[0] || "?"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name}</div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                    <select
                      data-testid={`role-select-${u.user_id}`}
                      value={u.role}
                      onChange={(e) => changeRole(u.user_id, e.target.value)}
                      className="px-3 py-2 rounded-full border border-slate-200 bg-white text-xs capitalize focus:border-seafoam outline-none shrink-0"
                    >
                      <option value="traveler">Traveler</option>
                      <option value="partner">Partner</option>
                      <option value="admin">Admin</option>
                    </select>
                    <label className="btn-pill bg-slate-900 text-white text-xs cursor-pointer shrink-0">
                      Upload
                      <input data-testid={`admin-upload-${u.user_id}`} type="file" onChange={(e) => e.target.files?.[0] && onAdminUpload(u.user_id, e.target.files[0])} className="hidden" />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
